import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiTrendingUp, FiBarChart3, FiCalendar, FiUsers, FiDollarSign, FiEdit, FiCheckSquare, FiClock, FiHeart, FiAward, FiStar } = FiIcons;

export const questionSections = [
  {
    id: 'personal_info',
    title: 'Getting Started',
    description: 'Let\'s begin by capturing some basic information about you and your business.',
    icon: FiUsers,
    questions: [
      {
        id: 'name',
        type: 'text',
        text: 'What is your name?',
        placeholder: 'Enter your full name',
        required: true
      },
      {
        id: 'business_name',
        type: 'text',
        text: 'What is your business name?',
        placeholder: 'Enter your business or brand name',
        required: false
      },
      {
        id: 'business_stage',
        type: 'select',
        text: 'What stage is your business currently in?',
        required: true,
        options: [
          { value: 'idea', label: 'Just an idea - haven\'t started yet' },
          { value: 'startup', label: 'Startup - launched within the last year' },
          { value: 'growth', label: 'Growth stage - established and scaling' },
          { value: 'mature', label: 'Mature - well-established business' }
        ]
      }
    ]
  },
  {
    id: 'five_ones',
    title: 'The Rule of 5 Ones',
    description: 'Define your core business focus using the proven Rule of 5 Ones framework.',
    icon: FiTarget,
    questions: [
      {
        id: 'target_market',
        type: 'textarea',
        text: 'Who is your ONE target market?',
        subtitle: 'Be as specific as possible. Who exactly do you serve?',
        placeholder: 'e.g., Female entrepreneurs aged 25-40 who want to start online businesses',
        required: true,
        rows: 3
      },
      {
        id: 'offer',
        type: 'textarea',
        text: 'What is your ONE main offer?',
        subtitle: 'What is the primary product or service you will focus on?',
        placeholder: 'e.g., Online course teaching digital marketing fundamentals',
        required: true,
        rows: 3
      },
      {
        id: 'conversion_mechanism',
        type: 'select',
        text: 'What is your ONE conversion mechanism?',
        subtitle: 'How will you primarily sell your offer?',
        required: true,
        options: [
          { value: 'checkout_page', label: 'Direct checkout page' },
          { value: 'webinars', label: 'Webinars' },
          { value: 'sales_calls', label: 'Sales calls' },
          { value: 'dms', label: 'Direct messages' },
          { value: 'email_sequence', label: 'Email sequence' },
          { value: 'linkedin_outreach', label: 'LinkedIn outreach' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        id: 'problem_solved',
        type: 'textarea',
        text: 'What is the ONE main problem you solve?',
        subtitle: 'What specific pain point does your offer address?',
        placeholder: 'e.g., Helping people overcome the overwhelm of starting an online business',
        required: true,
        rows: 3
      },
      {
        id: 'launch_date',
        type: 'text',
        text: 'When will you launch (or continue selling) this offer?',
        subtitle: 'Commit to a specific month and day',
        placeholder: 'e.g., February 15, 2026',
        required: true
      },
      {
        id: 'price_point',
        type: 'number',
        text: 'What will be your price point?',
        subtitle: 'Popular price points: $17, $27, $47, $57, $97, $197, $997',
        placeholder: '97',
        min: 1,
        required: true
      }
    ]
  },
  {
    id: 'audit_2025',
    title: '2025 Business Audit',
    description: 'Let\'s analyze what worked (or what you plan to focus on if you\'re just starting).',
    icon: FiBarChart3,
    questions: [
      {
        id: 'best_month_revenue',
        type: 'text',
        text: 'What was your best month in 2025 (revenue-wise)?',
        subtitle: 'If you haven\'t started yet, what month do you plan to launch?',
        placeholder: 'e.g., October - $5,000 or Planning to launch in March',
        required: false
      },
      {
        id: 'success_factors',
        type: 'textarea',
        text: 'What contributed to that success (or what will you focus on)?',
        subtitle: 'What specific actions, strategies, or content drove results?',
        placeholder: 'e.g., Consistent posting, viral TikTok video, email campaign, LinkedIn outreach, etc.',
        required: false,
        rows: 3
      },
      {
        id: 'audience_growth',
        type: 'textarea',
        text: 'What helped you grow your audience the most this year?',
        subtitle: 'Or what strategies do you plan to use for audience growth?',
        placeholder: 'e.g., Instagram Reels, collaborations, LinkedIn articles, consistent value-driven content',
        required: false,
        rows: 3
      },
      {
        id: 'top_content_types',
        type: 'checkbox',
        text: 'What content types performed best (or do you plan to focus on)?',
        required: false,
        options: [
          { value: 'reels', label: 'Instagram/TikTok Reels' },
          { value: 'carousels', label: 'Carousel posts' },
          { value: 'stories', label: 'Stories' },
          { value: 'live_videos', label: 'Live videos' },
          { value: 'blog_posts', label: 'Blog posts' },
          { value: 'email_newsletters', label: 'Email newsletters' },
          { value: 'youtube_videos', label: 'YouTube videos' },
          { value: 'podcasts', label: 'Podcasts' },
          { value: 'linkedin_posts', label: 'LinkedIn posts/articles' }
        ]
      },
      {
        id: 'consistency_challenges',
        type: 'textarea',
        text: 'What is keeping you from being consistent?',
        subtitle: 'Identify the main obstacles to consistent action',
        placeholder: 'e.g., Lack of time, perfectionism, unclear priorities, distractions',
        required: false,
        rows: 3
      }
    ]
  },
  {
    id: 'revenue_planning',
    title: 'Revenue Planning',
    description: 'Let\'s calculate your specific revenue targets and daily action goals.',
    icon: FiDollarSign,
    questions: [
      {
        id: 'monthly_income_goal',
        type: 'number',
        text: 'What is your monthly income goal for 2026?',
        subtitle: 'Be realistic but ambitious',
        placeholder: '10000',
        min: 0,
        required: true
      },
      {
        id: 'conversion_rate',
        type: 'number',
        text: 'What conversion rate do you expect?',
        subtitle: 'If unsure, 2% is a good starting point',
        placeholder: '2',
        min: 0.1,
        max: 50,
        step: 0.1,
        required: true
      }
    ]
  },
  {
    id: 'content_audit',
    title: 'Content Performance Analysis',
    description: 'Analyze your top-performing content to inform your 2026 strategy.',
    icon: FiEdit,
    questions: [
      {
        id: 'top_post_1',
        type: 'textarea',
        text: 'Describe your top-performing post #1',
        subtitle: 'If you haven\'t posted yet, describe a content idea you\'re excited about',
        placeholder: 'Describe the post content, format, and key message',
        required: false,
        rows: 2
      },
      {
        id: 'top_post_1_reason',
        type: 'textarea',
        text: 'Why did this post perform well?',
        subtitle: 'Or why do you think this content idea would work?',
        placeholder: 'e.g., Relatable story, actionable tips, trending audio, professional insights, etc.',
        required: false,
        rows: 2
      },
      {
        id: 'top_post_2',
        type: 'textarea',
        text: 'Describe your top-performing post #2',
        placeholder: 'Describe the post content, format, and key message',
        required: false,
        rows: 2
      },
      {
        id: 'top_post_2_reason',
        type: 'textarea',
        text: 'Why did this post perform well?',
        placeholder: 'Analyze what made it successful',
        required: false,
        rows: 2
      },
      {
        id: 'content_themes',
        type: 'checkbox',
        text: 'What content themes resonate most with your audience?',
        required: false,
        options: [
          { value: 'educational', label: 'Educational/How-to content' },
          { value: 'behind_scenes', label: 'Behind-the-scenes' },
          { value: 'personal_stories', label: 'Personal stories' },
          { value: 'tips_tricks', label: 'Quick tips and tricks' },
          { value: 'motivation', label: 'Motivational content' },
          { value: 'industry_news', label: 'Industry news/trends' },
          { value: 'user_generated', label: 'User-generated content' },
          { value: 'product_demos', label: 'Product demonstrations' },
          { value: 'professional_insights', label: 'Professional insights/expertise' }
        ]
      }
    ]
  },
  {
    id: 'posting_schedule',
    title: 'Posting Schedule',
    description: 'Choose a realistic posting schedule that you can maintain consistently.',
    icon: FiCalendar,
    questions: [
      {
        id: 'posting_level',
        type: 'radio',
        text: 'Which posting schedule will you commit to?',
        subtitle: 'Start with Level 1 and work your way up. Consistency beats quantity!',
        required: true,
        options: [
          { 
            value: 'level_1', 
            label: 'Level 1: 1 piece of content at 7 AM PST daily' 
          },
          { 
            value: 'level_2', 
            label: 'Level 2: 2 pieces of content daily (7 AM & 12 PM PST)' 
          },
          { 
            value: 'level_3', 
            label: 'Level 3: 3 pieces of content daily (7 AM, 12 PM & 4 PM PST)' 
          }
        ]
      },
      {
        id: 'content_batch_day',
        type: 'select',
        text: 'What day of the week will you batch create content?',
        required: true,
        options: [
          { value: 'sunday', label: 'Sunday' },
          { value: 'monday', label: 'Monday' },
          { value: 'tuesday', label: 'Tuesday' },
          { value: 'wednesday', label: 'Wednesday' },
          { value: 'thursday', label: 'Thursday' },
          { value: 'friday', label: 'Friday' },
          { value: 'saturday', label: 'Saturday' }
        ]
      }
    ]
  },
  {
    id: 'daily_actions',
    title: 'Daily Actions',
    description: 'Define the specific tasks you\'ll do every day to build momentum.',
    icon: FiCheckSquare,
    questions: [
      {
        id: 'daily_priority_tasks',
        type: 'checkbox',
        text: 'Select your daily PRIORITY tasks (choose 3-5 max)',
        subtitle: 'These are non-negotiable tasks you\'ll do every single day',
        required: true,
        options: [
          { value: 'post_content', label: 'Post scheduled content' },
          { value: 'respond_comments', label: 'Respond to comments' },
          { value: 'respond_dms', label: 'Respond to DMs' },
          { value: 'engage_audience', label: 'Engage with my audience' },
          { value: 'research_content', label: 'Research new content ideas' },
          { value: 'check_analytics', label: 'Check previous day\'s analytics' },
          { value: 'follow_up_leads', label: 'Follow up with leads' },
          { value: 'update_social_media', label: 'Update social media stories' },
          { value: 'network_linkedin', label: 'Network on LinkedIn' }
        ]
      },
      {
        id: 'daily_extra_tasks',
        type: 'checkbox',
        text: 'Select your daily EXTRA tasks (if time allows)',
        subtitle: 'These are bonus tasks you\'ll do when you have extra time',
        required: false,
        options: [
          { value: 'network_online', label: 'Network with other creators' },
          { value: 'learn_new_skills', label: 'Learn new skills/watch tutorials' },
          { value: 'competitor_research', label: 'Research competitors' },
          { value: 'brainstorm_ideas', label: 'Brainstorm new business ideas' },
          { value: 'organize_content', label: 'Organize content files' },
          { value: 'plan_tomorrow', label: 'Plan tomorrow\'s tasks' },
          { value: 'linkedin_engagement', label: 'Engage with LinkedIn connections' }
        ]
      }
    ]
  },
  {
    id: 'weekly_actions',
    title: 'Weekly Actions',
    description: 'Plan your weekly tasks for strategic growth and planning.',
    icon: FiClock,
    questions: [
      {
        id: 'weekly_priority_tasks',
        type: 'checkbox',
        text: 'Select your weekly PRIORITY tasks',
        required: true,
        options: [
          { value: 'plan_content', label: 'Plan content for the upcoming week' },
          { value: 'batch_create', label: 'Batch create content' },
          { value: 'review_analytics', label: 'Review weekly analytics' },
          { value: 'batch_film', label: 'Batch film videos/reels' },
          { value: 'email_list', label: 'Send email to list' },
          { value: 'goal_review', label: 'Review and adjust weekly goals' },
          { value: 'linkedin_articles', label: 'Write and publish LinkedIn articles' }
        ]
      },
      {
        id: 'weekly_extra_tasks',
        type: 'checkbox',
        text: 'Select your weekly EXTRA tasks',
        required: false,
        options: [
          { value: 'shoot_broll', label: 'Shoot b-roll content' },
          { value: 'organize_files', label: 'Organize files and assets' },
          { value: 'research_trends', label: 'Research new trends' },
          { value: 'collaborate', label: 'Reach out for collaborations' },
          { value: 'update_website', label: 'Update website/landing pages' },
          { value: 'celebrate_wins', label: 'Celebrate weekly wins' },
          { value: 'linkedin_network', label: 'Expand LinkedIn network' }
        ]
      }
    ]
  },
  {
    id: 'monthly_actions',
    title: 'Monthly Actions',
    description: 'Set up monthly reviews and strategic planning sessions.',
    icon: FiCalendar,
    questions: [
      {
        id: 'monthly_priority_tasks',
        type: 'checkbox',
        text: 'Select your monthly PRIORITY tasks',
        required: true,
        options: [
          { value: 'review_best_content', label: 'Review last month\'s best content' },
          { value: 'set_monthly_goals', label: 'Set goals for the new month' },
          { value: 'analyze_metrics', label: 'Deep dive into analytics' },
          { value: 'plan_content_calendar', label: 'Plan next month\'s content calendar' },
          { value: 'financial_review', label: 'Review financial performance' },
          { value: 'strategy_adjustment', label: 'Adjust strategy based on results' }
        ]
      },
      {
        id: 'monthly_extra_tasks',
        type: 'checkbox',
        text: 'Select your monthly EXTRA tasks',
        required: false,
        options: [
          { value: 'celebrate_achievements', label: 'Celebrate 3 major wins' },
          { value: 'network_events', label: 'Attend networking events' },
          { value: 'skill_development', label: 'Invest in skill development' },
          { value: 'remove_unproductive', label: 'Remove unproductive tasks' },
          { value: 'team_check_in', label: 'Team check-in (if applicable)' },
          { value: 'competitor_analysis', label: 'Comprehensive competitor analysis' },
          { value: 'linkedin_strategy', label: 'Review and optimize LinkedIn strategy' }
        ]
      }
    ]
  },
  {
    id: 'tracking_metrics',
    title: 'Tracking & Analytics',
    description: 'Identify the key metrics you\'ll track to measure success.',
    icon: FiTrendingUp,
    questions: [
      {
        id: 'primary_metrics',
        type: 'checkbox',
        text: 'Which metrics will you track monthly? (Select 3-5 most important)',
        required: true,
        options: [
          { value: 'revenue', label: 'Revenue' },
          { value: 'followers', label: 'Follower count' },
          { value: 'posts_published', label: 'Number of posts published' },
          { value: 'website_traffic', label: 'Website traffic' },
          { value: 'conversion_rate', label: 'Conversion rate' },
          { value: 'email_subscribers', label: 'Email subscribers' },
          { value: 'engagement_rate', label: 'Engagement rate' },
          { value: 'leads_generated', label: 'Leads generated' },
          { value: 'linkedin_connections', label: 'LinkedIn connections' },
          { value: 'linkedin_engagement', label: 'LinkedIn post engagement' }
        ]
      },
      {
        id: 'tracking_method',
        type: 'radio',
        text: 'How will you track these metrics?',
        required: true,
        options: [
          { value: 'spreadsheet', label: 'Simple spreadsheet' },
          { value: 'analytics_tools', label: 'Analytics tools (Google Analytics, etc.)' },
          { value: 'social_insights', label: 'Social media platform insights' },
          { value: 'linkedin_analytics', label: 'LinkedIn analytics' },
          { value: 'crm_system', label: 'CRM system' },
          { value: 'combination', label: 'Combination of methods' }
        ]
      }
    ]
  },
  {
    id: 'commitment',
    title: 'Commitment Statement',
    description: 'Create your personal commitment to make 2026 your best year yet.',
    icon: FiHeart,
    questions: [
      {
        id: 'word_for_2026',
        type: 'text',
        text: 'What is your ONE word for 2026?',
        subtitle: 'Choose a word that will guide your decisions and actions',
        placeholder: 'e.g., Focus, Growth, Consistency, Courage, Impact',
        required: true
      },
      {
        id: 'how_show_up',
        type: 'textarea',
        text: 'How will you show up differently in 2026?',
        subtitle: 'What behaviors, habits, or mindset will you adopt?',
        placeholder: 'e.g., I will show up with more confidence, consistency, and clarity in my messaging',
        required: true,
        rows: 3
      },
      {
        id: 'non_negotiable',
        type: 'textarea',
        text: 'What is your non-negotiable for 2026?',
        subtitle: 'What will you absolutely not compromise on?',
        placeholder: 'e.g., Daily content creation, morning routine, family time',
        required: true,
        rows: 2
      },
      {
        id: 'end_of_year_vision',
        type: 'textarea',
        text: 'At the end of 2026, what do you want to be able to say?',
        subtitle: 'Paint a picture of your success',
        placeholder: 'e.g., I built a sustainable business that gives me freedom and impact',
        required: true,
        rows: 3
      },
      {
        id: 'why_2026',
        type: 'textarea',
        text: 'What is your WHY for making 2026 extraordinary?',
        subtitle: 'What deeper purpose drives your commitment?',
        placeholder: 'e.g., To create financial freedom for my family and inspire others to pursue their dreams',
        required: true,
        rows: 3
      }
    ]
  },
  {
    id: 'confidence_check',
    title: 'Confidence & Readiness',
    description: 'Let\'s assess your confidence and identify any final considerations.',
    icon: FiStar,
    questions: [
      {
        id: 'confidence_level',
        type: 'scale',
        text: 'How confident are you about achieving your 2026 goals?',
        subtitle: 'Be honest - this helps us understand where you might need more support',
        min: 1,
        max: 10,
        scaleLabels: { min: 'Not confident', max: 'Extremely confident' },
        required: true
      },
      {
        id: 'biggest_challenge',
        type: 'textarea',
        text: 'What do you think will be your biggest challenge in 2026?',
        subtitle: 'Identifying potential obstacles helps you prepare for them',
        placeholder: 'e.g., Time management, staying consistent, overcoming fear of visibility',
        required: false,
        rows: 3
      },
      {
        id: 'support_needed',
        type: 'checkbox',
        text: 'What kind of support would help you succeed?',
        required: false,
        options: [
          { value: 'accountability', label: 'Accountability partner/group' },
          { value: 'education', label: 'More education/training' },
          { value: 'tools', label: 'Better tools and systems' },
          { value: 'community', label: 'Supportive community' },
          { value: 'mentorship', label: 'Mentorship/coaching' },
          { value: 'time_management', label: 'Time management strategies' },
          { value: 'mindset_work', label: 'Mindset and confidence work' },
          { value: 'linkedin_networking', label: 'LinkedIn networking groups' }
        ]
      },
      {
        id: 'commitment_signature',
        type: 'text',
        text: 'Type your name as your digital signature to commit to this plan',
        subtitle: 'This represents your commitment to following through on your 2026 gameplan',
        placeholder: 'Your full name',
        required: true
      }
    ]
  }
];